/*
 * Project: bellcloud
 * File: components/ui/common/DashboardBanner.tsx
 * Description: Dashboard notification banner for alarms and alerts
 */

import React from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';

interface DashboardBannerProps {
  deviceCount: number;
  type?: 'alarm' | 'alert';
  className?: string;
}

const BannerContainer = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing[6]}px ${({ theme }) => theme.spacing[7]}px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: ${({ theme }) => theme.borderRadius.lg}px;
  background: ${({ theme }) => theme.colors.surface.group.brand};
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[1]}px;
`;

const MainText = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: Inter;
  font-size: ${({ theme }) => theme.fontSize.md}px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: 130%;
`;

const SubText = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  opacity: 0.7;
  font-family: Inter;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: 130%;
`;

const ActionButton = styled.button`
  display: flex;
  padding: ${({ theme }) => theme.spacing[4]}px ${({ theme }) => theme.spacing[6]}px;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  background: ${({ theme }) => theme.colors.button.bg.primary.default};
  color: ${({ theme }) => theme.colors.button.text.primary};
  font-family: Inter;
  font-size: ${({ theme }) => theme.fontSize.md}px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: 130%;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.button.bg.primary.hover};
  }

  &:active {
    transform: translateY(1px);
  }
`;

export const DashboardBanner: React.FC<DashboardBannerProps> = ({
  deviceCount,
  type = 'alarm',
  className,
}) => {
  const navigate = useNavigate();

  const handleGoToRealtime = () => {
    navigate('/monitoring');
  };

  return (
    <BannerContainer className={className}>
      <TextContainer>
        <MainText>
          {deviceCount} device{deviceCount !== 1 ? 's are' : ' is'} currently in {type}
        </MainText>
        <SubText>Review alarms, warnings, and upcoming maintenance items</SubText>
      </TextContainer>
      <ActionButton onClick={handleGoToRealtime}>Go to Realtime</ActionButton>
    </BannerContainer>
  );
};
